import { type FormContainerOnError, type FormSchema, type FormSetup, type FormSubmitValues } from '@if/mui'
import { Stack } from '@mui/material'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { useUpdateBanner } from '@src/data/api/merchants-api/merchants-api'
import { BannerDto, BannerSchema } from '@src/data/types/BannerSchema'
import { Merchant } from '@src/data/types/Merchant'
import { AxiosError } from 'axios'
import { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { TabFormMethods } from '../../../EditMerchant/components/tabs/MerchantGeneralTab'
import { useSyncFormDefaults } from '../../../EditMerchant/components/useSyncFormDefaults'
import BannerInformationPanel from '../BannerInformationPanel'
import BannerPropertiesOwnershipPanel from '../BannerPropertiesOwnershipPanel'
import BannerReportConfigPanel from '../BannerReportConfigPanel'
import { getBannerDtoFromMerchant } from './bannerDto'

export type BannerSchemaType = typeof BannerSchema

/** Config a tab hook returns; spread onto a `<MultiFormTab>`. Mirrors `MerchantTabConfig<S>`. */
export interface BannerTabConfig<S extends FormSchema> {
  useFormSetup: () => FormSetup<S>
  onSubmit: (values: FormSubmitValues<S>, methods: TabFormMethods<S>) => unknown
  onError?: FormContainerOnError
  children: (methods: TabFormMethods<S>) => React.ReactNode
}

const BannerGeneralTabContent = ({ defaults }: { defaults: BannerDto }) => {
  const [featureFlags] = useFeatureFlags()
  const { control, trigger } = useFormContext<BannerDto>()
  useSyncFormDefaults<BannerDto>(defaults)

  return (
    <Stack gap={2}>
      <BannerInformationPanel control={control} />
      <BannerReportConfigPanel control={control} trigger={trigger} />
      {featureFlags?.useCustomFlashPlan && <BannerPropertiesOwnershipPanel control={control} />}
    </Stack>
  )
}

/** General tab config. Save stays on the page; the tab container's Cancel handles navigation back. */
export function useBannerGeneralTab(banner: Merchant): BannerTabConfig<BannerSchemaType> {
  const { t } = useTranslation()
  const [updateBanner] = useUpdateBanner()

  const defaults = useMemo(() => getBannerDtoFromMerchant(banner), [banner])

  const useFormSetup = useCallback(
    (): FormSetup<BannerSchemaType> => ({
      schema: BannerSchema,
      defaultValues: defaults,
    }),
    [defaults],
  )

  const onSubmit = useCallback(
    async (values: BannerDto, methods: TabFormMethods<BannerSchemaType>) => {
      await updateBanner(values)
      methods.reset(values)
    },
    [updateBanner],
  )

  // Generic backend error ({ message } body, kind `unknown`); translate the code
  // it carries. Other kinds (e.g. 409) fall through to the app registry.
  const onError = useMemo<FormContainerOnError>(
    () => ({
      unknown: ({ raw }) => {
        const message = (raw as AxiosError<{ message: string }>).response?.data?.message || 'unknown'
        return { role: 'contextual', message: t(`common.errors.${message}`, 'An error occurred') }
      },
    }),
    [t],
  )

  const children = useCallback(() => <BannerGeneralTabContent defaults={defaults} />, [defaults])

  return { useFormSetup, onSubmit, onError, children }
}
